import {
    ScenarioRunCheck,
    ScenarioRunEvent,
    ScenarioRunPhase,
    ScenarioRunScenario,
    ScenarioRunState,
} from '../../../api/api';

/**
 * Whether a scenario was part of the run at all. A scenario the run filter left out, or that is disabled in the
 * suite, is in the state document only so the suite reads complete; it says nothing about this run.
 */
export const isInRun = (scenario: ScenarioRunScenario) =>
    scenario.step !== 'not selected';

export const checkKind = (status?: string) =>
    status === 'passed' ? 'passed' : status === 'skipped' ? 'skipped' : 'failed';

/**
 * The scenario's errors that are not a failed check said again. The runner writes one error per failed check, and
 * the check itself already shows expected and actual next to each other.
 */
export const errorsBesideChecks = (scenario: ScenarioRunScenario) => {
    const failed = (scenario.checks ?? []).filter(
        (check) => checkKind(check.status) === 'failed'
    );

    return (scenario.errors ?? []).filter(
        (error) =>
            !failed.some((check) =>
                error.startsWith(`${check.oracle}: ${check.name} expected`)
            )
    );
};

/** The warning a passed check raised, which the runner writes as "oracle: name: detail". */
const warningOf = (check: ScenarioRunCheck) => `${check.oracle}: ${check.name}: ${check.detail}`;

/** The scenario's warnings that do not belong to a check, which show on the check itself. */
export const warningsBesideChecks = (scenario: ScenarioRunScenario) => {
    const fromChecks = (scenario.checks ?? [])
        .filter((check) => check.detail)
        .map(warningOf);

    return (scenario.warnings ?? []).filter(
        (warning) => !fromChecks.includes(warning)
    );
};

/** Whether a passed check raised a warning on its scenario. */
export const checkWarns = (scenario: ScenarioRunScenario, check: ScenarioRunCheck) =>
    checkKind(check.status) === 'passed' &&
    !!check.detail &&
    (scenario.warnings ?? []).includes(warningOf(check));

export const durationMs = (from?: string, to?: string) => {
    if (!from) {
        return 0;
    }

    const value =
        (to ? new Date(to).getTime() : Date.now()) - new Date(from).getTime();

    return isFinite(value) && value > 0 ? value : 0;
};

/** A duration as people say it: 4s, 1m 52s, 1h 05m. */
export const formatDuration = (milliseconds: number) => {
    const seconds = Math.round(milliseconds / 1000);

    if (seconds < 60) {
        return `${seconds}s`;
    }

    if (seconds < 3600) {
        return `${Math.floor(seconds / 60)}m ${String(seconds % 60).padStart(2, '0')}s`;
    }

    const minutes = Math.floor(seconds / 60);

    return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, '0')}m`;
};

// A stretch shorter than this that is over says nothing: the waits after a payment from Exact, whose documents were
// done before the stretch began, used to show as "0s" in the timeline and the bar.
const EMPTY_PHASE_MS = 1000;

/** Whether a stretch is worth a row or a legend entry: still going, or over after at least a second. */
export const phaseSaysSomething = (phase: { startedAtUtc: string; finishedAtUtc?: string }) =>
    !phase.finishedAtUtc || durationMs(phase.startedAtUtc, phase.finishedAtUtc) >= EMPTY_PHASE_MS;

/** What a stretch of time is, for the colour and the short label in a bar. */
export type PhaseKind = 'setup' | 'chargebee' | 'waiting' | 'connector' | 'checks' | 'scenario';

export interface Phase {
    id: string;
    label: string;
    /** What happened in it, as the runner names it. */
    name: string;
    title: string;
    kind: PhaseKind;
    startedAtUtc: string;
    finishedAtUtc?: string;
}

// Short labels for the bar, and the sentence that says what the time was spent on.
const PHASE_LOOK: Record<string, { label: string; kind: PhaseKind; hint?: string }> = {
    preflight: { label: 'Pre-flight', kind: 'setup' },
    arrange: { label: 'Chargebee', kind: 'chargebee' },
    act: { label: 'Act', kind: 'chargebee' },
    'arrange-settles': { label: 'Arrange settles', kind: 'waiting' },
    webhook: {
        label: 'Webhook to the connector',
        kind: 'waiting',
        hint: "From the document in Chargebee until its webhook reaches the connector's save-chargebee: Chargebee sending it, and anything the site posts through on the way.",
    },
    'connector-receives': {
        label: 'Reaching the connector',
        kind: 'waiting',
        hint: "From the document in Chargebee until the connector holds it: Chargebee's webhook and the connector's own receive side. This run could not tell them apart.",
    },
    'connector-receives-split': {
        label: 'Connector receiving',
        kind: 'connector',
        hint: 'From the webhook reaching the connector until it holds the document. A document it cannot handle yet, for instance while its customer is not in Exact yet, is put back for 30 s and tried again.',
    },
    'customer-to-exact': {
        label: 'Customer to Exact',
        kind: 'connector',
        hint: "The connector holds an invoice back until its customer is in Exact, and only the Exact send app puts it there. A long stretch here is that app not getting to the customer: a cold start, or other messages ahead of it in its queue.",
    },
    'connector-books': { label: 'Booking in Exact', kind: 'connector' },
    checks: { label: 'Checking', kind: 'checks' },
};

const lookOf = (id: string) => {
    if (PHASE_LOOK[id]) {
        return PHASE_LOOK[id];
    }

    if (id.startsWith('profile-') && id.endsWith('-settles')) {
        return {
            label: 'Profile takes effect',
            kind: 'waiting' as PhaseKind,
            hint: 'The connector caches its configuration; every instance has re-read it after this wait.',
        };
    }

    if (id.startsWith('profile-')) {
        return { label: 'Profile', kind: 'setup' as PhaseKind };
    }

    return { label: id, kind: 'setup' as PhaseKind };
};

const toPhase = (phase: ScenarioRunPhase, split = false): Phase => {
    // Once the webhook's arrival is known, the stretch after it is the connector's own receive side.
    const look = lookOf(split && phase.id === 'connector-receives' ? 'connector-receives-split' : phase.id ?? '');
    const attempts = /(\d+) attempts/.exec(phase.name ?? '')?.[1];

    return {
        id: phase.id ?? '',
        label: attempts ? `${look.label} (${attempts} attempts)` : look.label,
        name: phase.name || look.label,
        title: look.hint ? `${phase.name}. ${look.hint}` : phase.name ?? look.label,
        kind: look.kind,
        startedAtUtc: phase.startedAtUtc ?? '',
        finishedAtUtc: phase.finishedAtUtc,
    };
};

/**
 * Reads a scenario's phases from its log, for a run from before the runner recorded them. The lines it looks for
 * are the ones the runner writes at each turn of a scenario.
 */
const phasesFromLog = (
    scenario: ScenarioRunScenario,
    events: ScenarioRunEvent[]
): ScenarioRunPhase[] => {
    if (!scenario.startedAtUtc) {
        return [];
    }

    const at = (matches: (message: string) => boolean) =>
        events.find((event) => matches(event.message ?? ''))?.atUtc;

    const waiting = at((message) =>
        message.startsWith('waiting for the connector to finish')
    );
    const reached = at(
        (message) =>
            message.includes('received by the connector') ||
            message.includes('booked in Exact')
    );
    const reading = at((message) => message.startsWith('reading the result back'));
    const end = scenario.finishedAtUtc;

    const points: { id: string; name: string; at?: string }[] = [
        { id: 'arrange', name: 'Creating the documents in Chargebee', at: scenario.startedAtUtc },
        { id: 'connector-receives', name: 'Waiting for the connector to receive the documents', at: waiting },
        { id: 'connector-books', name: 'Connector booking in Exact', at: reached },
        { id: 'checks', name: 'Reading the result back and checking it', at: reading },
    ].filter((point) => !!point.at);

    return points.map((point, index) => ({
        id: point.id,
        name: point.name,
        startedAtUtc: point.at,
        finishedAtUtc: points[index + 1]?.at ?? end,
    }));
};

/** The phases of a scenario, and whether they were read from its log rather than recorded. */
export const scenarioPhases = (
    scenario: ScenarioRunScenario,
    events: ScenarioRunEvent[]
): { phases: Phase[]; fromLog: boolean } => {
    const recorded = scenario.phases ?? [];

    if (recorded.length > 0) {
        const split = recorded.some((phase) => phase.id === 'webhook');

        return { phases: recorded.map((phase) => toPhase(phase, split)), fromLog: false };
    }

    return { phases: phasesFromLog(scenario, events).map((phase) => toPhase(phase)), fromLog: true };
};

/**
 * Where the whole run spent its time: its own stretches - the pre-flight, profiles taking effect - and each scenario
 * that ran as one stretch. A run from before the runner recorded its stretches shows what came before the first
 * scenario as one.
 */
export const runPhases = (state: ScenarioRunState): Phase[] => {
    const scenarios = (state.scenarios ?? [])
        .filter((scenario) => isInRun(scenario) && scenario.startedAtUtc)
        .map(
            (scenario): Phase => ({
                id: `scenario-${scenario.id}`,
                label: scenario.id ?? '',
                name: scenario.id ?? '',
                title: scenario.description ?? scenario.id ?? '',
                kind: 'scenario',
                startedAtUtc: scenario.startedAtUtc as string,
                finishedAtUtc: scenario.finishedAtUtc,
            })
        );

    const own = (state.phases ?? []).map((phase) => toPhase(phase));

    if (own.length === 0 && state.startedAtUtc && scenarios.length > 0) {
        own.push({
            id: 'before-scenarios',
            label: 'Pre-flight and profiles',
            name: 'Pre-flight and profiles',
            title: 'Checking the environment and applying the profiles, before the first scenario',
            kind: 'setup',
            startedAtUtc: state.startedAtUtc,
            finishedAtUtc: scenarios[0].startedAtUtc,
        });
    }

    return [...own, ...scenarios].sort(
        (a, b) => new Date(a.startedAtUtc).getTime() - new Date(b.startedAtUtc).getTime()
    );
};
